import { z } from 'zod';
import {
  ControlKindSchema,
  type ProseOrigin,
  ProseOriginSchema,
  ProvenanceBasisSchema,
} from './enums';
import { QrsControlIdSchema } from './ids';
import { EvidenceCitationSchema } from './locator';
import { AttributeRefSchema } from './pack';
import { NonEmptyStringSchema } from './primitives';

/**
 * What a finding **says**, as slots rather than as a sentence
 * (docs/DECISIONS.md ZD-043).
 *
 * ## This names a grammar that already existed
 *
 * `renderDraft` had a slot grammar from the day it was written. It was unnamed,
 * untyped and duplicated: the title welded a pattern's title to a subject
 * resolved from the model, the description was the pattern's own text verbatim,
 * the rationale was either a read-back of the witnesses or a list of what could
 * not be decided, and three separate functions independently joined
 * `undetermined` into a phrase with three different wordings. So this is **not a
 * second renderer beside the one that exists** — it is the intermediate the
 * existing renderer computed and discarded, and `renderStatement` is now the
 * only thing that turns any of it into words.
 *
 * The commit that introduced this file changed no output at all: all 96 corpus
 * drafts stayed byte-identical against `finding-drafts-snapshot.json`. That is
 * the guard, and it is what makes the numbers in the commit *after* it mean
 * something.
 *
 * ## The rule that keeps it a transcript
 *
 * A slot may carry **authored** text by identity — a pattern's `description` —
 * and may never carry **generated** text. Everything else it holds is a
 * reference: an object id, an attribute ref, a citation. That is the same line
 * `prose_origin` draws one layer up (ZD-031 §2), and it is what stops a slot
 * record from quietly becoming the place a claim is invented.
 *
 * ## It is a derivation, not a second copy of the model
 *
 * A `FindingStatement` is recomputed from the canonical model on every render
 * and is not persisted beside the finding, so it cannot drift from the facts it
 * reads — the failure ZD-017 refused for assessment state and ZD-027 rule 3
 * refused for per-extraction crossing derivation. The `basis` a ground clause
 * carries is a *build-time* decision, which is why it is recorded here rather
 * than re-derived by the renderer, and it does not outlive the read.
 */

/**
 * The slots, and the three that are deliberately absent.
 *
 * **`threat_source` is not here.** AWS Threat Composer makes it the subject of
 * the sentence; measured against this canonical model it is fillable on **2 of
 * 96** matches, and never once via an actor — Zevik's predicates reach actors
 * almost entirely in boosts, and a boost grounds only when it carried the match
 * (ZD-029 §5). The decisive test is that all three unestablished-slot policies
 * are wrong for it: omitting does nothing 98% of the time, rendering a gap puts
 * *"we cannot say who could do this"* on 94 findings, and asking the client is a
 * question the design cannot answer and a threat model exists to answer. When
 * every policy is wrong, the thing is not a slot — it is the pattern's own
 * claim, and it is already in `description` where QRS authored it (ZD-043 §2).
 * The counter-case is real and is recorded with it: a threat statement with no
 * actor is a weaker sentence, and QRS does know the actor for every pattern, so
 * an authored `threat_source` on `ThreatPattern` remains available. It is a
 * pack-authoring change to 65 patterns and would have moved this session's
 * numbers for a second reason.
 *
 * **`impacted_goal` is not here**, and the reasoning generalises past this
 * slot. Threat Composer's is CIA. Zevik's two candidates would each put **a
 * hole in the sentence caused by our own taxonomy rather than by a client
 * fact**: `stride_category` is null for two of three MVP domains *by design*
 * (ZD-009), so a goal slot would show a gap on 24 of 96 matches for a lens
 * reason; and `RiskDimensions` (ZD-011) holds frequencies and loss magnitudes
 * with no member naming what is protected. Any future slot reading
 * `stride_category` inherits the same problem, which is why this is written
 * here rather than in a commit message.
 *
 * **`threat_impact` is not a separate slot**, because it is inside the
 * pattern's `description` and splitting it means re-splitting 65 authored
 * paragraphs — ZD-035 §4.1's rule that carrying what was said in the shape it
 * was said beats inventing structure the author did not write. Impact as a
 * *score* already has a home in `default_scoring_inputs`, and a third place for
 * it would be ZD-017's fact that can disagree with the other two.
 */
export const SLOT_KINDS = [
  /** The canonical object the predicate was evaluated against. */
  'subject',
  /** What the pattern says is wrong. Pack-authored, verbatim. */
  'threat_action',
  /** Why the pattern held here: the witnesses, with basis and citation. */
  'grounds',
  /** What could not be decided, where the verdict is `unknown`. */
  'undecided',
  /** The trust boundaries the subject flow crosses. */
  'crossed_boundary',
  /** The assets the evaluation reached. */
  'impacted_assets',
  /** What the model observes protecting the subject, three-valued. */
  'observed_controls',
  /** What would mitigate it. Catalogue-authored, verbatim, caveats included. */
  'control_direction',
] as const;
export const SlotKindSchema = z.enum(SLOT_KINDS);
export type SlotKind = z.infer<typeof SlotKindSchema>;

/**
 * Three states, and the third is the one that carries information.
 *
 * `unestablished` and `not_applicable` are **different claims** and only one of
 * them is about the client's documents: *nobody stated this* against *there is
 * nothing here to state*. Collapsing them is ZD-036 §2's failure one layer out.
 * On a `true` verdict an empty `grounds` means the predicate consulted no
 * attribute — a fact about this system. On an `unknown` verdict it is
 * structurally impossible for there to be any witness at all, because an
 * `unknown` outcome combines only its undecided parts and every leaf returning
 * `unknown` returns none — a fact about the evaluator, and not something a
 * client could answer.
 */
export const SLOT_STATES = ['established', 'unestablished', 'not_applicable'] as const;
export const SlotStateSchema = z.enum(SLOT_STATES);
export type SlotState = z.infer<typeof SlotStateSchema>;

/**
 * Whether the model can say anything protects the subject.
 *
 * **Three-valued, and the third value closes in prose what ZD-039 closed in the
 * predicate.** `controlsCovering` is undeterminate on 32 of 85 corpus subjects
 * (ZD-041 §3), and rendering *"no control was observed"* over an undetermined
 * coverage is the false-negative mechanism reappearing one layer out — a report
 * telling a client that nothing protects an object when what is true is that we
 * could not tell. That it *would* have shipped that way is the evidence that
 * **the prose layer needs its own version of every predicate-layer rule rather
 * than inheriting them by assumption** (ZD-043 §5).
 *
 * `none_observed` is a **derived** established-empty, legitimate for exactly
 * ZD-027 rule 2's reason: the evaluation ran and had what it needed, and the
 * state where it did not is `unestablished`, reported separately.
 */
export const CONTROL_COVERAGE_VERDICTS = ['covered', 'none_observed'] as const;
export const ControlCoverageVerdictSchema = z.enum(CONTROL_COVERAGE_VERDICTS);
export type ControlCoverageVerdict = z.infer<typeof ControlCoverageVerdictSchema>;

/** One fact a verdict rested on, read back with where it came from. */
export const GroundClauseSchema = z.object({
  object_id: z.string(),
  attribute: NonEmptyStringSchema,
  /**
   * `null` where the witness named an object the canonical index could not
   * resolve, so there is no provenance to read.
   *
   * **Unreachable from `evaluatePredicate`**, which only ever witnesses
   * attributes of targets it navigated to, and retained anyway: deleting a
   * defensive branch during a refactor whose entire claim is that it changes no
   * output would be a behaviour change smuggled into the one commit that must
   * not contain one.
   */
  basis: ProvenanceBasisSchema.nullable(),
  citations: z.array(EvidenceCitationSchema),
});
export type GroundClause = z.infer<typeof GroundClauseSchema>;

const SlotBase = {
  state: SlotStateSchema,
  /**
   * Who wrote this slot's words. `rendered` is a transcript of the model or a
   * verbatim carry of authored text; `authored` is a model's contextualisation
   * (ZD-031 §2, ZD-043 §6). Per slot rather than per finding, because after the
   * LLM half lands a finding is a mixture and a single scalar cannot say which
   * sentence a model wrote.
   *
   * **This is not the same axis as `Provenance.asserted_by_run_id`, and merging
   * them would be a defect** (ZD-046 §6). `origin` says who wrote the
   * *sentence*; provenance plus its run says who established the *fact*. They
   * cross: a slot rendering a fact an LLM extractor established is still
   * `rendered`, because the sentence is a transcript of the model — which is
   * ZD-031 §2's whole distinction, and the reason a rendered sentence over
   * model-extracted facts is not an authored one.
   *
   * The temptation is real because on today's data the two axes agree
   * everywhere: nothing is `authored` and no fact has a run, so one field would
   * appear to serve both and every test would pass. It stops being true on the
   * first LLM call. See CLAUDE.md, *"two axes that look like one"*.
   */
  origin: ProseOriginSchema,

  /**
   * The sentence a model or a reviewer wrote, **beside** the transcript rather
   * than over it (docs/DECISIONS.md ZD-061 §2).
   *
   * `null` where nobody has written one, which is every slot in the system
   * today. Where somebody has, the slot carries both: the structured content
   * the pipeline derived, and the words a person or a model put in front of a
   * client.
   *
   * **It is a field beside rather than a replacement, and ZD-021's mirror
   * question run both ways is what decides that.** *Where does an edit go when a
   * reviewer makes one?* — here. *What does the record say when they do not?* —
   * `null`, honestly. Had an edit instead overwritten the rendered fields, the
   * question **"what did the pipeline actually say"** would be unrecoverable
   * afterwards — and that is the one question an evidence pack exists to answer
   * (DESIGN_SPEC §15), so the shape that loses it is not an option however
   * convenient.
   *
   * **A citation is not editable through this, and that is structural rather
   * than checked.** The citations in `grounds.clauses` and
   * `observed_controls.examined` are claims about a *document*, not about a
   * sentence, so a reviewer rewording a clause changes what is said and not what
   * was found. There is no path from an edit to a `Citation` because an edit
   * carries only text.
   */
  authored_text: NonEmptyStringSchema.nullable(),
};

export const SlotSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('subject'), ...SlotBase, object_id: z.string() }),
  z.object({
    kind: z.literal('threat_action'),
    ...SlotBase,
    /**
     * The pattern's own `title` and `description`, verbatim, in that order.
     *
     * Both here rather than the short one reaching the renderer by a second
     * route, so that `renderStatement` needs the statement and the model and
     * nothing else. A renderer that also had to be handed the pattern would
     * make the statement an incomplete account of what a finding says, which is
     * the one thing it is for.
     */
    label: z.string(),
    text: z.string(),
  }),
  z.object({ kind: z.literal('grounds'), ...SlotBase, clauses: z.array(GroundClauseSchema) }),
  z.object({ kind: z.literal('undecided'), ...SlotBase, refs: z.array(AttributeRefSchema) }),
  z.object({
    kind: z.literal('crossed_boundary'),
    ...SlotBase,
    boundary_ids: z.array(z.string()),
    /**
     * `inferred` in practice, because ZD-027 rule 3 derives the crossing rather
     * than reading it from a document — which is why a finding that rests on a
     * boundary-mediated fact reads as less confident than one that does not,
     * with no confidence rule added.
     */
    basis: ProvenanceBasisSchema.nullable(),
  }),
  z.object({ kind: z.literal('impacted_assets'), ...SlotBase, asset_ids: z.array(z.string()) }),
  z.object({
    kind: z.literal('observed_controls'),
    ...SlotBase,
    /**
     * The claim, and the only field that carries one. `covering`, `examined`
     * and `undetermined` are the **working** — why the answer is what it is —
     * and they may be present whatever the state, because a reason is not an
     * assertion. Keeping the claim in one discriminant is ZD-036 §2's rule: an
     * empty `covering` must never be the thing a reader has to interpret.
     */
    coverage: ControlCoverageVerdictSchema.nullable(),
    /** What the pattern looked for, derived from its own predicate. */
    kinds_sought: z.array(ControlKindSchema),
    /** Observed controls of a sought kind that cover the subject. */
    covering: z.array(z.string()),
    /**
     * The per-control readings behind the answer.
     *
     * These are witnesses that would otherwise sit in `grounds`, where they
     * made up **160 of the corpus's 248 witness clauses** and rendered as up to
     * sixteen flat clauses about unrelated controls. They are kept rather than
     * dropped because a negative rests on every control having been checked
     * (ZD-029 §5) and because their citations are part of the finding's
     * evidence — collapsing the *sentence* must not shrink what it cites.
     */
    examined: z.array(GroundClauseSchema),
    /** The attributes that make the question undecidable (ZD-041 §3). */
    undetermined: z.array(AttributeRefSchema),
  }),
  z.object({
    kind: z.literal('control_direction'),
    ...SlotBase,
    control_ids: z.array(QrsControlIdSchema),
    /**
     * `controlOutputLines` output, **by identity**.
     *
     * The efficacy caveat and the legal-obligation framing reach anything
     * client-facing through this field and no other, and a softened paraphrase
     * is refused by a test comparing them against the catalogue's own
     * sentences. Before this slot existed `controlOutputLines` had **no
     * production caller at all** — the seam that exists so the caveat "cannot
     * be dropped" was reachable only from its own unit test.
     */
    lines: z.array(NonEmptyStringSchema),
  }),
]);
export type Slot = z.infer<typeof SlotSchema>;

/**
 * Whether a slot is carrying content, whatever it claims about itself.
 *
 * `observed_controls` is deliberately judged on `coverage` alone: its other
 * fields are the working rather than the claim, and an undecidable coverage
 * question that names *which* controls it could not scope is more useful than
 * one that names nothing, so those refs must survive on a slot that is not
 * established.
 */
function slotIsPopulated(slot: Slot): boolean {
  switch (slot.kind) {
    case 'subject':
      return slot.object_id !== '';
    case 'threat_action':
      return slot.label !== '' && slot.text !== '';
    case 'grounds':
      return slot.clauses.length > 0;
    case 'undecided':
      return slot.refs.length > 0;
    case 'crossed_boundary':
      return slot.boundary_ids.length > 0 && slot.basis !== null;
    case 'impacted_assets':
      return slot.asset_ids.length > 0;
    case 'observed_controls':
      return slot.coverage !== null;
    case 'control_direction':
      return slot.control_ids.length > 0 && slot.lines.length > 0;
  }
}

/**
 * True where a slot's state and its content disagree.
 *
 * The ZD-036 §2 rule applied to the statement, in both directions: a slot that
 * is not `established` must be empty, so an absence can never dress itself as a
 * claim, and an `established` slot must carry something, so a claim can never
 * dress itself as an absence.
 */
export function slotStateContradictsContent(slot: Slot): boolean {
  return slot.state === 'established' ? !slotIsPopulated(slot) : slotIsPopulated(slot);
}

/**
 * What the renderer does with a slot that is applicable and unestablished
 * (ZD-043 §4).
 *
 * **Declared once, per slot, with an argument each** — a single policy would be
 * wrong for at least one of them, and a convention would be wrong for all of
 * them the first time somebody added a slot without thinking about it.
 *
 *  - `gap` — say so. The absence is a fact and omitting it reads as a denial.
 *  - `omit` — say nothing, and only where the absence is **already stated
 *    elsewhere in the same finding**, because saying it twice is the
 *    third-fact-that-can-disagree shape (ZD-017).
 *
 * There is deliberately no `clarification` member. A renderer that raised a
 * question would not be pure (a finding's prose must be a function of the
 * finding), and it would put a new question population on top of the 165 the
 * pack service already produces. Where an absence is answerable, the statement
 * records it and something downstream asks.
 */
export const SLOT_POLICY: Record<SlotKind, 'gap' | 'omit'> = {
  /* Mandatory; guaranteed by `PatternMatch` and so never unestablished. */
  subject: 'gap',
  threat_action: 'gap',
  control_direction: 'gap',
  /*
   * On a `true` verdict with no witnesses the pattern applied structurally, and
   * a finding that silently omitted its own reason would read like one with an
   * evidenced reason.
   */
  grounds: 'gap',
  undecided: 'gap',
  /*
   * **The one slot where omission is right**, and the argument is that its
   * absence is never silent: where a pattern reads the crossing and the model
   * never established it, the verdict is already `unknown` and the `undecided`
   * slot names the attribute — 21 of the corpus's 92 undetermined refs. Saying
   * it a second time is two records of one fact (ZD-017). Where the pattern does
   * not read it at all the slot is `not_applicable`, which is a different claim
   * and also renders nothing.
   */
  crossed_boundary: 'omit',
  /*
   * The privacy question. Answerable from a document, given a field by ZD-036
   * precisely so it could be answered, and omitting it reads as *no assets are
   * affected* — ZD-021's manufactured absence in the direction CLAUDE.md
   * principle 9 names as the primary failure mode.
   */
  impacted_assets: 'gap',
  /* Never omit: see `CONTROL_COVERAGE_VERDICTS`, on ZD-039's own argument. */
  observed_controls: 'gap',
};

/**
 * Which slots a **model** may write, and which a **reviewer** may edit — two
 * tables, because they are two questions (docs/DECISIONS.md ZD-063 §2).
 *
 * **Settled before anything crosses it**, because the alternative is
 * discovering the boundary when a model rewrites a sentence it should not have.
 *
 * ## Why this was one constant and is now two
 *
 * ZD-043 §6 named two authorable slots and ZD-061 §2 established that a
 * reviewer may edit exactly what a model may write — *"what differs is the
 * origin marker and the audit, not the permission"*. That was right on the
 * evidence available: both of the retired `SLOT_AUTHORABLE`'s refusal reasons
 * (the slot carries an architecture claim; the text must survive verbatim) held
 * against a person and a model, and no third reason was in view.
 *
 * A third reason arrived with the contextualiser, and it is measured rather
 * than argued. `threat_action` carries the pattern's own QRS-authored,
 * human-reviewed `label` and `description`, and across the corpus **102
 * findings carry 24 distinct descriptions** — the text is a function of
 * `threat_pattern_id` and of nothing about the client. A model authoring it
 * therefore produces 102 unreviewed variants of 24 reviewed sentences, or else
 * imports client facts that are `grounds`'s to state. And `label` is the only
 * finding text a report renders, carried verbatim into `QuestionBearing.label`
 * as well, so an authored label makes two copies of one verbatim carry
 * disagree.
 *
 * That reason applies to a model and not to a person: a reviewer editing the
 * pattern's sentence for one client is QRS contextualising its own pack
 * content, which is the consultancy this product sells, and it is visible as
 * `approver_edited` with a named approver behind it.
 *
 * **The two tables agree on every row but one, and that is exactly why they are
 * separated now.** One constant serving two questions is the shape CLAUDE.md
 * records four times over — a representation doing two jobs, correct on all
 * current data, wrong the first time the data can tell them apart. Here the
 * data could not tell them apart until a model could write a slot at all.
 */
export const SLOT_MODEL_AUTHORABLE: Record<SlotKind, boolean> = {
  subject: false,
  /* See above: QRS-authored, human-reviewed, and constant within its pattern. */
  threat_action: false,
  grounds: true,
  undecided: false,
  crossed_boundary: false,
  impacted_assets: false,
  observed_controls: false,
  /*
   * The case that proves the boundary has to exist before anything crosses it:
   * this slot carries catalogue text with an efficacy caveat that must not be
   * paraphrased softer, and *"pseudonymised data remains personal data under UK
   * GDPR"* softened by a contextualiser is a client acting on an over-claim.
   */
  control_direction: false,
};

/**
 * What a reviewer may rewrite (ZD-061 §2, unchanged in substance).
 *
 * Note what editing `threat_action` means: it is the pattern's authored text
 * carried verbatim, so a reviewer editing it is contextualising QRS pack
 * content for one client — legitimate consultancy, and exactly why it has to be
 * visible as `approver_edited` rather than blending into `rendered`.
 */
export const SLOT_REVIEWER_EDITABLE: Record<SlotKind, boolean> = {
  subject: false,
  threat_action: true,
  grounds: true,
  undecided: false,
  crossed_boundary: false,
  impacted_assets: false,
  observed_controls: false,
  control_direction: false,
};

/** Which table governs a given origin. `rendered` is nobody writing anything. */
export const AUTHORABLE_BY_ORIGIN: Record<
  Exclude<ProseOrigin, 'rendered'>,
  Record<SlotKind, boolean>
> = {
  authored: SLOT_MODEL_AUTHORABLE,
  approver_edited: SLOT_REVIEWER_EDITABLE,
};

/**
 * The order slots are rendered in. Fixed, and **not** a permutation table.
 *
 * AWS Threat Composer hard-codes eight renderings selected by which slots are
 * populated. That works where slots are user-typed free text and the statement
 * is one sentence; here the fill is *computed* from a three-valued evaluation
 * over a canonical model, so an enumerated table would be a second copy of the
 * fill rule with nothing checking it against the first — ZD-029 §1's argument
 * for deriving `depends_on_attributes` rather than declaring it beside the
 * predicate. So the shape is generated from this order plus `SLOT_POLICY`, and
 * the count of realised shapes is published as a measurement instead.
 */
export const SLOT_ORDER: readonly SlotKind[] = [
  'subject',
  'threat_action',
  'grounds',
  'undecided',
  'crossed_boundary',
  'impacted_assets',
  'observed_controls',
];

/**
 * Slots that are built and deliberately not rendered into a finding's prose,
 * with the reason — because an omission and a decision must not have the same
 * shape (ZD-035 §2).
 *
 * `control_direction` is the only member. `CandidateFinding` has no field for
 * mitigation text and is not given one here: DESIGN_SPEC §7.7 gives mitigation
 * its own object — a `ControlMapping` from finding to control, rendered as a
 * `Recommendation` — so carrying the catalogue's sentences on the finding as
 * well would be a second place control text lives, which is the shape ZD-017
 * refused for assessment state.
 *
 * What the slot does now is make the catalogue's obligations reachable from the
 * finding's own statement, under a test that they survive by identity. That is
 * strictly more than the previous state, in which `controlOutputLines` had no
 * production caller and the caveat could not reach anything at all. sds-05
 * renders it.
 */
export const SLOT_NOT_RENDERED: readonly SlotKind[] = ['control_direction'];

export const FindingStatementSchema = z
  .object({
    threat_pattern_id: NonEmptyStringSchema,
    subject_id: z.string(),
    slots: z.array(SlotSchema),
  })
  .superRefine((statement, ctx) => {
    const seen = new Set<SlotKind>();
    statement.slots.forEach((slot, i) => {
      if (seen.has(slot.kind)) {
        ctx.addIssue({
          code: 'custom',
          message: `duplicate slot ${slot.kind}`,
          path: ['slots', i, 'kind'],
        });
      }
      seen.add(slot.kind);

      if (slotStateContradictsContent(slot)) {
        ctx.addIssue({
          code: 'custom',
          message:
            slot.state === 'established'
              ? `slot ${slot.kind} is established and carries nothing`
              : `slot ${slot.kind} is ${slot.state} and carries content — an absence must not be ` +
                `able to dress itself as a claim`,
          path: ['slots', i],
        });
      }

      /*
       * The claim and the working must agree. A slot naming controls that cover
       * the subject while claiming none was observed is the ZD-039 failure
       * written into a data structure rather than into a predicate.
       */
      if (slot.kind === 'observed_controls' && slot.covering.length > 0) {
        if (slot.coverage !== 'covered') {
          ctx.addIssue({
            code: 'custom',
            message: `observed_controls names covering controls but claims ${String(slot.coverage)}`,
            path: ['slots', i, 'coverage'],
          });
        }
      }

      /*
       * **Who may write this slot is asked per origin, because a model and a
       * reviewer are no longer the same question** (ZD-063 §2). Both refusal
       * reasons ZD-061 §2 named still hold against a person — a slot carrying
       * an architecture claim is a transcript of the canonical model, and a
       * reviewer changing it makes the sentence disagree with the model every
       * downstream reader still reads (ZD-017); `control_direction` carries
       * catalogue text that must survive verbatim whoever typed it. What
       * changed is that a third reason exists which applies to a model alone,
       * so the two tables differ on `threat_action` and on nothing else.
       */
      if (slot.origin !== 'rendered' && !AUTHORABLE_BY_ORIGIN[slot.origin][slot.kind]) {
        ctx.addIssue({
          code: 'custom',
          message:
            `slot ${slot.kind} may not be written by ${slot.origin === 'authored' ? 'a model' : 'a reviewer'}: ` +
            `it carries either a fact about the client's architecture, text that must survive ` +
            `verbatim, or pack text a model may not rewrite`,
          path: ['slots', i, 'origin'],
        });
      }

      /*
       * **An absence may not acquire a sentence** (ZD-063 §2), and this rule is
       * the one thing in this file that `slotStateContradictsContent` cannot
       * say for itself.
       *
       * That function reads `slotIsPopulated`, which looks only at the
       * transcript fields — `grounds.clauses`, `impacted_assets.asset_ids` and
       * the rest — because `authored_text` sits *beside* the transcript rather
       * than replacing it (see `SlotBase`). Correct for what it is for, and it
       * leaves a hole this file's whole subject walks straight into: a slot
       * whose state is `not_applicable` carries no clauses, so an authored
       * sentence on it satisfies both directions of the content rule and the
       * statement parses.
       *
       * Measured on the corpus when the contextualiser was designed, that is
       * `grounds` on **62 of 102** findings — every undecided verdict, where an
       * `unknown` outcome carries no witnesses by construction and the state is
       * `not_applicable` for that structural reason (`SLOT_STATES`). A model
       * writing there states grounds for a finding whose own statement says
       * there are none: ZD-036 §2's absence dressed as a claim, arriving
       * through the one field designed to sit beside the transcript.
       *
       * Folding `authored_text` into `slotIsPopulated` is the wrong fix and was
       * tried on paper first: it would let an `established` slot be satisfied by
       * a sentence with no transcript behind it, which is the same defect with
       * the sign flipped.
       */
      if (slot.authored_text !== null && slot.state !== 'established') {
        ctx.addIssue({
          code: 'custom',
          message:
            `slot ${slot.kind} is ${slot.state} and carries a written sentence: an absence must ` +
            `not acquire one, because nothing established what the sentence would be about`,
          path: ['slots', i, 'authored_text'],
        });
      }

      /*
       * The origin and the text agree, in both directions — ZD-036 §2's rule
       * applied to who wrote a sentence. A slot claiming somebody wrote it and
       * carrying nothing they wrote is an empty claim; one carrying written text
       * while calling itself a transcript hides the writer, which is the whole
       * thing this field exists to prevent.
       */
      if (slot.origin === 'rendered' && slot.authored_text !== null) {
        ctx.addIssue({
          code: 'custom',
          message: `slot ${slot.kind} carries written text but calls itself a transcript`,
          path: ['slots', i, 'authored_text'],
        });
      }
      if (slot.origin !== 'rendered' && slot.authored_text === null) {
        ctx.addIssue({
          code: 'custom',
          message: `slot ${slot.kind} says it was written by ${slot.origin} and carries no text`,
          path: ['slots', i, 'authored_text'],
        });
      }
    });

    /*
     * Every slot, every time. A statement missing a slot and a statement whose
     * slot is `not_applicable` are different claims, and only the second says
     * anything — the same distinction `SLOT_STATES` exists to keep.
     */
    for (const kind of SLOT_KINDS) {
      if (seen.has(kind)) continue;
      ctx.addIssue({ code: 'custom', message: `statement omits slot ${kind}`, path: ['slots'] });
    }
  });
export type FindingStatement = z.infer<typeof FindingStatementSchema>;

/**
 * The finding-level `prose_origin`, **derived** (ZD-043 §6).
 *
 * Not stored beside the per-slot origins: two records of one fact can disagree,
 * and the disagreement would be invisible (ZD-017). `rendered` means every slot
 * is a transcript, which is exactly what the field has meant since ZD-031 §2 —
 * so nothing downstream changes, and what is gained is that the other two can
 * say *which* sentence somebody wrote.
 *
 * **Precedence is human, then model, then pipeline** (ZD-061 §2), and it is a
 * choice rather than an ordering accident: the strongest claim available about a
 * finding's prose is that a named person wrote some of it, and that is the one a
 * reader most needs. A finding with one reviewer-edited slot and forty
 * model-authored ones is still one a person put their name to.
 */
export function statementProseOrigin(statement: FindingStatement): ProseOrigin {
  if (statement.slots.some((slot) => slot.origin === 'approver_edited')) return 'approver_edited';
  if (statement.slots.some((slot) => slot.origin === 'authored')) return 'authored';
  return 'rendered';
}

/**
 * Every origin present in a statement.
 *
 * Exists so a client-facing provenance sentence can be **computed** from what a
 * report contains rather than asserted about it. `RENDERED_ONLY_LIMIT` told
 * every client *"No language model wrote any of it"* unconditionally, which is
 * true today and false on the first authored or reviewer-edited slot — an
 * assertion in the one paragraph a reader most relies on, in a package whose own
 * `statementProseOrigin` already computed the answer (ZD-061 §6).
 */
export function statementOrigins(statement: FindingStatement): Set<ProseOrigin> {
  return new Set(statement.slots.map((slot) => slot.origin));
}

/** One sentence somebody wrote, for a slot they are permitted to write. */
export interface AuthoredSlotText {
  slot: SlotKind;
  text: string;
}

/**
 * The statement with written sentences laid **beside** its transcript
 * (docs/DECISIONS.md ZD-063 §1).
 *
 * The one place either a model's contextualisation or a reviewer's edit lands,
 * so that "what did the pipeline actually say" survives whatever anybody wrote
 * over it (ZD-061 §2) and so that the two writers cannot acquire two mechanisms.
 *
 * **It re-parses, and the parse is the enforcement.** Every rule about who may
 * write a slot, about an absence not acquiring a sentence, and about origin and
 * text agreeing lives in `FindingStatementSchema` — a caller assembling slots by
 * hand would have to reproduce all three, which is how two copies of one rule
 * start. Rejected rather than repaired, in `validateFinding`'s posture (ZD-029
 * §6): the acceptance layer upstream is what turns a refusal into a finding that
 * keeps its rendered prose, and reaching here with an answer the schema refuses
 * is a wiring defect rather than a bad answer.
 */
export function withAuthoredSlots(
  statement: FindingStatement,
  answers: readonly AuthoredSlotText[],
  origin: Exclude<ProseOrigin, 'rendered'>,
): FindingStatement {
  if (answers.length === 0) return statement;
  const byKind = new Map(answers.map((answer) => [answer.slot, answer.text]));
  return FindingStatementSchema.parse({
    ...statement,
    slots: statement.slots.map((slot) => {
      const text = byKind.get(slot.kind);
      return text === undefined ? slot : { ...slot, origin, authored_text: text };
    }),
  });
}

/** The slot of a given kind. Every statement carries all of them. */
export function slotOf<K extends SlotKind>(
  statement: FindingStatement,
  kind: K,
): Extract<Slot, { kind: K }> {
  const slot = statement.slots.find((candidate) => candidate.kind === kind);
  if (!slot) throw new Error(`statement has no ${kind} slot`);
  return slot as Extract<Slot, { kind: K }>;
}
